import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-[color,background-color,border-color,box-shadow,transform] duration-150 disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/40 focus-visible:ring-[3px] active:translate-y-px aria-invalid:ring-destructive/25 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "border border-primary/70 bg-primary text-primary-foreground shadow-sm shadow-primary/15 hover:bg-primary/88 hover:shadow-md hover:shadow-primary/20",
        primary: "border border-primary/70 bg-primary text-primary-foreground shadow-sm shadow-primary/15 hover:bg-primary/88 hover:shadow-md hover:shadow-primary/20",
        destructive:
          "border border-destructive/70 bg-destructive text-white shadow-sm shadow-destructive/15 hover:bg-destructive/85 focus-visible:ring-destructive/30",
        outline:
          "border border-border bg-surface/60 text-foreground shadow-xs hover:border-ring/45 hover:bg-accent hover:text-accent-foreground",
        secondary:
          "border border-secondary/70 bg-secondary text-secondary-foreground shadow-sm shadow-secondary/15 hover:bg-secondary/82",
        success: "border border-success/70 bg-success text-success-foreground shadow-sm shadow-success/15 hover:bg-success/85 focus-visible:ring-success/30",
        warning: "border border-warning/70 bg-warning text-warning-foreground shadow-sm shadow-warning/15 hover:bg-warning/85 focus-visible:ring-warning/30",
        info: "border border-info/70 bg-info text-info-foreground shadow-sm shadow-info/15 hover:bg-info/85 focus-visible:ring-info/30",
        subtle: "border border-transparent bg-muted text-foreground hover:bg-accent",
        ghost:
          "border border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:text-primary/80 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-7 rounded-md gap-1.5 px-2.5 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-7",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
